"use client";

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCharacterSheet } from "@/hooks/use-character-sheet";
import type { PartyPc } from "@/lib/core/schemas/party-view";
import type {
  PendingExpertiseChoiceDnd5e,
  PendingMetamagicChoiceDnd5e,
  PendingWeaponMasteryChoiceDnd5e,
} from "@/lib/rules/dnd5e/character/character-sheet";
import {
  LEVEL_CAP,
  xpForLevel,
} from "@/lib/rules/dnd5e/character/level-constants";
import {
  averageHpFor,
  canLevelUp,
  type HpGain,
  parseHpGain,
  validateAsiConfig,
} from "@/lib/rules/dnd5e/character/progression-logic";
import { Dnd5eSkillNameSchema } from "@/lib/rules/dnd5e/vocabulary/skill";
import {
  type FeatOptionDto as FeatOption,
  fetchFeatOptions,
} from "@/src/api/feats";
import { callMcpTool } from "@/src/api/mcp";
import {
  fetchWeaponMasteries,
  type WeaponMasteryOption,
} from "@/src/api/weapon-masteries";

type FeatConfigValue = number | string | string[];
type FeatConfig = Record<string, FeatConfigValue>;
type DependentChoiceField = NonNullable<FeatOption["choiceFields"]>[number] & {
  readonly kind: "dependent";
};

/** Type guard for the select_feat_5e error-ack `data: { variant }` shape
 * (the MCP `data` is `unknown`). Avoids an `as` cast on the untyped boundary. */
function isVariantData(obj: unknown): obj is { variant: string } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    typeof (obj as Record<string, unknown>).variant === "string"
  );
}

/** Type guard for the award_xp success-ack `data: { awarded, newTotal, reason }`
 * shape (the MCP `data` is `unknown`). Avoids an `as` cast on the untyped
 * boundary. */
function isAwardXpData(
  obj: unknown
): obj is { awarded: number; newTotal: number; reason: string } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    typeof (obj as Record<string, unknown>).awarded === "number" &&
    typeof (obj as Record<string, unknown>).newTotal === "number" &&
    typeof (obj as Record<string, unknown>).reason === "string"
  );
}

type SubclassOption = { id: string; label: string };
type FeatPool = "origin" | "general" | "fightingStyle";

type Phase =
  | "idle"
  | "choosing"
  | "revealing"
  | "done"
  | "error"
  | "choosing-feat"
  | "choosing-origin-feat"
  | "choosing-fighting-style-feat"
  | "configuring-asi"
  | "configuring-skilled"
  | "configuring-dependent-choice"
  | "applying-feat"
  | "feat-done"
  | "choosing-profile-proficiencies"
  | "applying-profile-proficiencies"
  | "profile-proficiencies-done"
  | "choosing-expertise"
  | "applying-expertise"
  | "expertise-done"
  | "choosing-metamagic"
  | "applying-metamagic"
  | "metamagic-done"
  | "choosing-weapon-masteries"
  | "applying-weapon-masteries"
  | "weapon-masteries-done"
  | "choosing-subclass"
  | "applying-subclass"
  | "subclass-done"
  | "awarding-input"
  | "awarding"
  | "award-done";

export function ProgressionPanel({
  pc,
  campaignId,
  onChange,
}: {
  pc: PartyPc | null;
  campaignId: string;
  onChange: () => unknown;
}) {
  const { sheet, mutate, isLoading } = useCharacterSheet(
    campaignId,
    pc?.id ?? null
  );
  const [phase, setPhase] = useState<Phase>("idle");
  const [reveal, setReveal] = useState<HpGain | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Non-blocking: the level-up committed server-side, but the post-success
  // sheet/party re-fetch failed — the reveal still shows, with a reload nudge.
  const [refreshFailed, setRefreshFailed] = useState(false);

  // Feat picker state
  const [selectedFeatId, setSelectedFeatId] = useState<string | null>(null);
  const [selectedFeatPool, setSelectedFeatPool] = useState<FeatPool | null>(
    null
  );
  const [asiConfig, setAsiConfig] = useState<Record<string, number>>({});
  const [skilledChoices, setSkilledChoices] = useState<Set<string>>(new Set());
  const [dependentField, setDependentField] =
    useState<DependentChoiceField | null>(null);
  const [dependentSource, setDependentSource] = useState<string | null>(null);
  const [dependentSingles, setDependentSingles] = useState<
    Record<string, string>
  >({});
  const [dependentMultis, setDependentMultis] = useState<
    Record<string, string[]>
  >({});
  const [featOptions, setFeatOptions] = useState<
    Record<FeatPool, readonly FeatOption[]>
  >({ origin: [], general: [], fightingStyle: [] });
  const [loadingFeatPool, setLoadingFeatPool] = useState<FeatPool | null>(null);
  const [selectedProfileChoiceId, setSelectedProfileChoiceId] = useState<
    string | null
  >(null);
  const [profileProficiencyChoices, setProfileProficiencyChoices] = useState<
    Set<string>
  >(new Set());
  const [selectedExpertiseChoice, setSelectedExpertiseChoice] =
    useState<PendingExpertiseChoiceDnd5e | null>(null);
  const [selectedExpertiseSkills, setSelectedExpertiseSkills] = useState<
    string[]
  >([]);
  const [selectedExpertiseTools, setSelectedExpertiseTools] = useState<
    string[]
  >([]);
  const [selectedMetamagicChoice, setSelectedMetamagicChoice] =
    useState<PendingMetamagicChoiceDnd5e | null>(null);
  const [selectedMetamagicOptions, setSelectedMetamagicOptions] = useState<
    string[]
  >([]);
  const [selectedWeaponMasteryChoice, setSelectedWeaponMasteryChoice] =
    useState<PendingWeaponMasteryChoiceDnd5e | null>(null);
  const [selectedWeaponMasteries, setSelectedWeaponMasteries] = useState<
    string[]
  >([]);
  const [weaponMasteryOptions, setWeaponMasteryOptions] = useState<
    readonly WeaponMasteryOption[]
  >([]);
  const [loadingWeaponMasteries, setLoadingWeaponMasteries] = useState(false);
  const [subclassOptions, setSubclassOptions] = useState<SubclassOption[]>([]);

  // Award XP form state
  const [awardAmount, setAwardAmount] = useState("");
  const [awardReason, setAwardReason] = useState("");
  const [awardResult, setAwardResult] = useState<{
    awarded: number;
    reason: string;
  } | null>(null);

  if (pc && isLoading) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  const cls = sheet?.classes[0]; // single-class (multiclass out of scope)
  if (!(pc && cls)) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        No class to advance yet.
      </div>
    );
  }

  const currentXp = sheet?.xp?.current ?? 0;
  const atCap = sheet.totalLevel >= LEVEL_CAP;
  const xpNeeded = atCap ? 0 : xpForLevel(sheet.totalLevel + 1);
  const xpShort = !atCap && currentXp < xpNeeded;
  const eligible = canLevelUp(sheet.totalLevel, currentXp);
  const avg = averageHpFor(cls.hitDie);

  const featSlots = sheet?.featSlots ?? {
    origin: 0,
    general: 0,
    fightingStyle: 0,
  };
  const pendingProfileProficiencyChoices =
    sheet.pendingProfileProficiencyChoices;
  const selectedProfileChoice =
    pendingProfileProficiencyChoices.find(
      (choice) => choice.profileId === selectedProfileChoiceId
    ) ?? null;

  // Scores for ASI validation. sheet.abilities crosses the untyped fetch
  // boundary, so guard the nested score + NaN before it reaches validateAsiConfig.
  const scoresFromSheet = Object.fromEntries(
    Object.entries(sheet?.abilities ?? {}).map(([a, v]) => {
      const score = v?.score;
      return [
        a,
        typeof score === "number" && !Number.isNaN(score) ? score : 10,
      ];
    })
  );
  const capsFromSheet = Object.fromEntries(
    Object.entries(sheet?.abilities ?? {}).map(([a, v]) => {
      const cap = v?.cap;
      return [a, typeof cap === "number" && !Number.isNaN(cap) ? cap : 20];
    })
  );

  // Capture stable locals before the async closure to satisfy TS strict
  // control-flow narrowing (pc/cls are consts narrowed above but TS may
  // re-widen them inside an async callback).
  const actorId = pc.id;
  const { classProfileId } = cls;

  async function choose(hpAcquisition: "roll" | "average") {
    setPhase("revealing");
    setErrorMsg(null);
    setRefreshFailed(false);
    let gain: HpGain | null = null;
    try {
      const ack = await callMcpTool(
        "level_up_5e",
        { actorId, classProfileId, hpAcquisition },
        campaignId
      );
      gain = parseHpGain(ack.data);
      if (!gain) {
        // The ack contract guarantees hpGain on success; if it's ever absent/
        // malformed, route to the error phase rather than a reveal-less dead-end.
        throw new Error("Level-up succeeded but the result couldn't be read.");
      }
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Level-up failed");
      setPhase("error");
      return;
    }
    // The level-up has COMMITTED server-side — show the reveal unconditionally.
    setReveal(gain);
    setPhase("done");
    // The post-success re-hydration is best-effort: a failure here does NOT mean
    // the level-up failed, so it must not flip us to the error phase.
    try {
      await mutate(); // re-hydrate HP + level lines (sheet DTO carries both)
      await onChange(); // refresh the party selector
    } catch {
      setRefreshFailed(true);
    }
  }

  async function applyFeat(featId: string, config?: FeatConfig) {
    setPhase("applying-feat");
    setErrorMsg(null);
    setRefreshFailed(false);
    try {
      const ack = await callMcpTool(
        "select_feat_5e",
        config ? { actorId, featId, config } : { actorId, featId },
        campaignId
      );
      // callMcpTool throws on ack.status === "ERROR"; defensively read variant if present.
      const variant =
        ack?.data && isVariantData(ack.data) ? ack.data.variant : undefined;
      if (variant) {
        throw new Error(`Couldn't apply feat (${variant}).`);
      }
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Feat selection failed");
      setPhase("error");
      return;
    }
    setPhase("feat-done");
    // Post-success re-hydration is best-effort — must NOT flip to error.
    try {
      await mutate();
      await onChange();
    } catch {
      setRefreshFailed(true);
    }
  }

  async function openFeatChooser(pool: FeatPool) {
    setErrorMsg(null);
    setSelectedFeatId(null);
    setSelectedFeatPool(pool);
    setAsiConfig({});
    setSkilledChoices(new Set());
    setDependentField(null);
    setDependentSource(null);
    setDependentSingles({});
    setDependentMultis({});
    setPhase(chooserPhaseForPool(pool));
    if (featOptions[pool].length > 0) {
      return;
    }
    setLoadingFeatPool(pool);
    try {
      const options = await fetchFeatOptions(pool);
      setFeatOptions((prev) => ({ ...prev, [pool]: options }));
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Feat options failed");
      setPhase("error");
    } finally {
      setLoadingFeatPool(null);
    }
  }

  async function applyProfileProficiencies() {
    if (!selectedProfileChoice) {
      return;
    }
    setPhase("applying-profile-proficiencies");
    setErrorMsg(null);
    setRefreshFailed(false);
    try {
      await callMcpTool(
        "select_profile_proficiencies_5e",
        {
          actorId,
          profileId: selectedProfileChoice.profileId,
          proficiencies: [...profileProficiencyChoices],
        },
        campaignId
      );
    } catch (e) {
      setErrorMsg(
        e instanceof Error ? e.message : "Profile proficiencies failed"
      );
      setPhase("error");
      return;
    }
    setPhase("profile-proficiencies-done");
    try {
      await mutate();
      await onChange();
    } catch {
      setRefreshFailed(true);
    }
  }

  async function applyExpertise() {
    if (
      selectedExpertiseChoice === null ||
      selectedExpertiseSkills.length + selectedExpertiseTools.length !==
        selectedExpertiseChoice.remaining
    ) {
      return;
    }
    setPhase("applying-expertise");
    setErrorMsg(null);
    setRefreshFailed(false);
    try {
      await callMcpTool(
        "select_expertise_5e",
        {
          actorId,
          classProfileId: selectedExpertiseChoice.classProfileId,
          skills: selectedExpertiseSkills,
          tools: selectedExpertiseTools,
        },
        campaignId
      );
    } catch (e) {
      setErrorMsg(
        e instanceof Error ? e.message : "Expertise selection failed"
      );
      setPhase("error");
      return;
    }
    setPhase("expertise-done");
    try {
      await mutate();
      await onChange();
    } catch {
      setRefreshFailed(true);
    }
  }

  async function applyMetamagic() {
    if (
      selectedMetamagicChoice === null ||
      selectedMetamagicOptions.length !== selectedMetamagicChoice.remaining
    ) {
      return;
    }
    setPhase("applying-metamagic");
    setErrorMsg(null);
    setRefreshFailed(false);
    try {
      for (const optionId of selectedMetamagicOptions) {
        await callMcpTool(
          "select_metamagic_5e",
          { actorId, optionId },
          campaignId
        );
      }
    } catch (e) {
      setErrorMsg(
        e instanceof Error ? e.message : "Metamagic selection failed"
      );
      setPhase("error");
      try {
        await mutate();
        await onChange();
      } catch {
        setRefreshFailed(true);
      }
      return;
    }
    setPhase("metamagic-done");
    try {
      await mutate();
      await onChange();
    } catch {
      setRefreshFailed(true);
    }
  }

  async function loadWeaponMasteryOptions() {
    if (weaponMasteryOptions.length > 0 || loadingWeaponMasteries) {
      return;
    }
    setLoadingWeaponMasteries(true);
    try {
      setWeaponMasteryOptions(await fetchWeaponMasteries());
    } catch (e) {
      setErrorMsg(
        e instanceof Error ? e.message : "Weapon mastery options failed"
      );
      setPhase("error");
    } finally {
      setLoadingWeaponMasteries(false);
    }
  }

  async function applyWeaponMasteries() {
    if (
      selectedWeaponMasteryChoice === null ||
      selectedWeaponMasteries.length !== selectedWeaponMasteryChoice.remaining
    ) {
      return;
    }
    setPhase("applying-weapon-masteries");
    setErrorMsg(null);
    setRefreshFailed(false);
    try {
      await callMcpTool(
        "select_weapon_masteries_5e",
        {
          actorId,
          classProfileId: selectedWeaponMasteryChoice.classProfileId,
          weaponKinds: selectedWeaponMasteries,
        },
        campaignId
      );
    } catch (e) {
      setErrorMsg(
        e instanceof Error ? e.message : "Weapon Mastery selection failed"
      );
      setPhase("error");
      return;
    }
    setPhase("weapon-masteries-done");
    try {
      await mutate();
      await onChange();
    } catch {
      setRefreshFailed(true);
    }
  }

  async function submitAwardXp() {
    const amount = Number(awardAmount);
    setPhase("awarding");
    setErrorMsg(null);
    setRefreshFailed(false);
    try {
      const reason =
        awardReason.trim().length > 0 ? awardReason.trim() : "GM award";
      const ack = await callMcpTool(
        "award_xp",
        { actorId, amount, reason },
        campaignId
      );
      if (!isAwardXpData(ack.data)) {
        throw new Error("XP awarded but the result couldn't be read.");
      }
      setAwardResult({ awarded: ack.data.awarded, reason: ack.data.reason });
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Award XP failed");
      setPhase("error");
      return;
    }
    setPhase("award-done");
    setAwardAmount("");
    setAwardReason("");
    // Post-success re-hydration is best-effort — must NOT flip to error.
    try {
      await mutate();
      await onChange();
    } catch {
      setRefreshFailed(true);
    }
  }

  // ASI stepper helpers
  const ASI_ABILITIES = ["str", "dex", "con", "int", "wis", "cha"] as const;

  const SKILLED_SKILL_OPTIONS = Dnd5eSkillNameSchema.options;

  function skillLabel(skill: string): string {
    return skill
      .split("-")
      .map((w) => (w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(" ");
  }

  function optionLabel(option: string): string {
    return skillLabel(option);
  }

  function masteryLabel(mastery: string): string {
    return skillLabel(mastery);
  }

  function resetExpertiseSelection() {
    setSelectedExpertiseChoice(null);
    setSelectedExpertiseSkills([]);
    setSelectedExpertiseTools([]);
  }

  function resetMetamagicSelection() {
    setSelectedMetamagicChoice(null);
    setSelectedMetamagicOptions([]);
  }

  function resetWeaponMasterySelection() {
    setSelectedWeaponMasteryChoice(null);
    setSelectedWeaponMasteries([]);
  }

  function isDependentChoiceField(
    field: NonNullable<FeatOption["choiceFields"]>[number]
  ): field is DependentChoiceField {
    return field.kind === "dependent";
  }

  function buildDependentConfig(): FeatConfig | null {
    if (!dependentSource || !dependentField) {
      return null;
    }

    const config: FeatConfig = { sourceClass: dependentSource };
    for (const [key, value] of Object.entries(dependentSingles)) {
      config[key] = value;
    }
    for (const [key, value] of Object.entries(dependentMultis)) {
      config[key] = value;
    }
    return config;
  }

  function chooserPhaseForPool(pool: FeatPool | null): Phase {
    if (pool === "origin") {
      return "choosing-origin-feat";
    }
    if (pool === "fightingStyle") {
      return "choosing-fighting-style-feat";
    }
    return "choosing-feat";
  }

  const revealedDependentFields =
    dependentField && dependentSource
      ? (dependentField.reveal[dependentSource] ?? [])
      : [];
  const dependentChoiceError =
    dependentField !== null &&
    dependentSource !== null &&
    revealedDependentFields.length === 0
      ? "No choices are available for this spell list."
      : null;
  const dependentChoicesComplete =
    dependentField !== null &&
    dependentSource !== null &&
    revealedDependentFields.length > 0 &&
    revealedDependentFields.every((field) => {
      if (field.kind === "single") {
        return (dependentSingles[field.key] ?? "").length > 0;
      }
      if (field.kind === "multi") {
        return (dependentMultis[field.key] ?? []).length === field.count;
      }
      return true;
    });

  function handleFeatClick(feat: FeatOption, pool: FeatPool) {
    setSelectedFeatId(feat.id);
    setSelectedFeatPool(pool);
    setErrorMsg(null);
    if (!feat.needsConfig) {
      applyFeat(feat.id);
    } else if (feat.configKind === "asi") {
      setAsiConfig({});
      setPhase("configuring-asi");
    } else if (feat.configKind === "skill-chooser") {
      setSkilledChoices(new Set());
      setPhase("configuring-skilled");
    } else if (feat.configKind === "dependent-choice") {
      const nextDependentField =
        feat.choiceFields?.find(isDependentChoiceField) ?? null;
      if (!nextDependentField) {
        setDependentField(null);
        setDependentSource(null);
        setDependentSingles({});
        setDependentMultis({});
        setErrorMsg("This feat is missing choice data.");
        setPhase(chooserPhaseForPool(pool));
        return;
      }
      setDependentField(nextDependentField);
      setDependentSource(null);
      setDependentSingles({});
      setDependentMultis({});
      setPhase("configuring-dependent-choice");
    } else {
      applyFeat(feat.id); // fallback: no config needed
    }
  }

  function adjustAsi(ability: string, delta: number) {
    setAsiConfig((prev) => {
      const next = { ...prev };
      const current = next[ability] ?? 0;
      const newVal = current + delta;
      if (newVal <= 0) {
        delete next[ability];
      } else {
        next[ability] = newVal;
      }
      return next;
    });
  }

  const asiValidation =
    phase === "configuring-asi"
      ? validateAsiConfig(asiConfig, scoresFromSheet, capsFromSheet)
      : { ok: false as const, reason: "" };
  const featChoiceDialogOpen =
    phase === "choosing-feat" ||
    phase === "choosing-origin-feat" ||
    phase === "choosing-fighting-style-feat" ||
    phase === "configuring-asi" ||
    phase === "configuring-skilled" ||
    phase === "configuring-dependent-choice" ||
    phase === "applying-feat" ||
    phase === "feat-done" ||
    phase === "choosing-profile-proficiencies" ||
    phase === "applying-profile-proficiencies" ||
    phase === "profile-proficiencies-done" ||
    phase === "choosing-expertise" ||
    phase === "applying-expertise" ||
    phase === "expertise-done" ||
    phase === "choosing-metamagic" ||
    phase === "applying-metamagic" ||
    phase === "metamagic-done" ||
    phase === "choosing-weapon-masteries" ||
    phase === "applying-weapon-masteries" ||
    phase === "weapon-masteries-done";
  const profileChoiceComplete =
    selectedProfileChoice !== null &&
    profileProficiencyChoices.size === selectedProfileChoice.count;
  const selectedExpertiseCount =
    selectedExpertiseSkills.length + selectedExpertiseTools.length;
  const expertiseChoiceComplete =
    selectedExpertiseChoice !== null &&
    selectedExpertiseCount === selectedExpertiseChoice.remaining;
  const metamagicChoiceComplete =
    selectedMetamagicChoice !== null &&
    selectedMetamagicOptions.length === selectedMetamagicChoice.remaining;
  const selectedWeaponMasteryKinds = new Set(
    selectedWeaponMasteryChoice?.selectedWeaponKinds ?? []
  );
  const availableWeaponMasteryOptions = selectedWeaponMasteryChoice
    ? weaponMasteryOptions.filter(
        (option) => !selectedWeaponMasteryKinds.has(option.weaponKind)
      )
    : [];
  const weaponMasteryChoiceComplete =
    selectedWeaponMasteryChoice !== null &&
    selectedWeaponMasteries.length === selectedWeaponMasteryChoice.remaining;

  return (
    <div className="my-2 space-y-3 rounded-lg border p-4 pb-16 md:pb-4">
      <div className="font-semibold text-foreground">
        {cls.className} — level {cls.level}
      </div>

      {!atCap && (
        <p className="text-muted-foreground text-sm">
          XP {currentXp} / {xpNeeded}
        </p>
      )}

      {atCap && (
        <p className="text-muted-foreground text-sm">Max level reached.</p>
      )}

      {xpShort && !atCap && (
        <p className="text-muted-foreground text-sm">
          Need {xpNeeded - currentXp} more XP to reach level {cls.level + 1}.
        </p>
      )}

      {errorMsg !== null && phase !== "error" && (
        <p className="text-destructive text-sm">{errorMsg}</p>
      )}

      {phase === "idle" && (
        <div className="border-t pt-3">
          <Button
            onClick={() => setPhase("awarding-input")}
            size="sm"
            type="button"
            variant="outline"
          >
            Award XP
          </Button>
        </div>
      )}

      {phase === "awarding-input" && (
        <div className="space-y-2 border-t pt-3">
          <div className="space-y-1">
            <label className="text-sm" htmlFor="award-xp-amount">
              Amount
            </label>
            <input
              className="w-full rounded border px-2 py-1 text-sm"
              id="award-xp-amount"
              inputMode="numeric"
              onChange={(e) => setAwardAmount(e.target.value)}
              type="number"
              value={awardAmount}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm" htmlFor="award-xp-reason">
              Reason
            </label>
            <input
              className="w-full rounded border px-2 py-1 text-sm"
              id="award-xp-reason"
              onChange={(e) => setAwardReason(e.target.value)}
              placeholder="GM award"
              type="text"
              value={awardReason}
            />
          </div>
          <div className="flex gap-2">
            <Button
              disabled={
                !Number.isInteger(Number(awardAmount)) ||
                Number(awardAmount) <= 0
              }
              onClick={submitAwardXp}
              size="sm"
              type="button"
            >
              Confirm
            </Button>
            <Button
              onClick={() => {
                setPhase("idle");
                setAwardAmount("");
                setAwardReason("");
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {phase === "awarding" && (
        <p className="text-muted-foreground text-sm">Awarding XP…</p>
      )}

      {phase === "award-done" && awardResult && (
        <div className="space-y-2 border-t pt-3">
          <p className="font-semibold text-foreground text-sm">
            +{awardResult.awarded} XP — {awardResult.reason}
          </p>
          {refreshFailed && (
            <p className="text-muted-foreground text-xs">
              Couldn't auto-refresh the sheet — reload to see updated stats.
            </p>
          )}
          <Button
            onClick={() => {
              setPhase("idle");
              setAwardResult(null);
              setRefreshFailed(false);
            }}
            size="sm"
            type="button"
            variant="ghost"
          >
            Done
          </Button>
        </div>
      )}

      {eligible && phase === "idle" && (
        <Button onClick={() => setPhase("choosing")} type="button">
          Level Up → {cls.level + 1}
        </Button>
      )}

      {eligible && phase === "choosing" && (
        <div className="space-y-2">
          <p className="text-sm">Choose your HP for level {cls.level + 1}:</p>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => choose("roll")}
              type="button"
              variant="outline"
            >
              Roll d{cls.hitDie}
            </Button>
            <Button
              onClick={() => choose("average")}
              type="button"
              variant="outline"
            >
              Take average ({avg})
            </Button>
          </div>
        </div>
      )}

      {phase === "revealing" && (
        <p className="text-muted-foreground text-sm">Advancing…</p>
      )}

      {phase === "done" && reveal && (
        <div className="space-y-2">
          <div className="space-y-1 text-sm">
            <div className="font-semibold">Level up!</div>
            <div>
              d{cls.hitDie} → {reveal.dieValue} ({reveal.mode}){" "}
              {reveal.conMod >= 0 ? "+" : ""}
              {reveal.conMod} CON → +{reveal.gain} HP
            </div>
            <div className="font-semibold">Max HP now {reveal.newMaxHp}</div>
          </div>
          {refreshFailed && (
            <p className="text-muted-foreground text-xs">
              Couldn't auto-refresh the sheet — reload to see updated stats.
            </p>
          )}
          <Button
            onClick={() => {
              setPhase("idle");
              setReveal(null);
              setRefreshFailed(false);
            }}
            size="sm"
            type="button"
            variant="ghost"
          >
            Done
          </Button>
        </div>
      )}

      {/* ── Subclass section ─────────────────────────────────────────────── */}
      {(sheet.subclassSlots ?? 0) > 0 && phase === "idle" && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center gap-2">
            <Badge>Subclass choice available</Badge>
          </div>
          <Button
            onClick={async () => {
              setPhase("choosing-subclass");
              try {
                const ack = await callMcpTool(
                  "get_offerable_subclasses_5e",
                  { actorId: pc.id },
                  campaignId
                );
                if (ack && ack.status === "ACK" && ack.data) {
                  setSubclassOptions(ack.data as SubclassOption[]);
                } else {
                  throw new Error(ack?.error || "Failed to load subclasses");
                }
              } catch (e) {
                setErrorMsg(
                  e instanceof Error ? e.message : "Failed to load subclasses"
                );
                setPhase("error");
              }
            }}
            size="sm"
            type="button"
            variant="outline"
          >
            Choose subclass
          </Button>
        </div>
      )}

      {phase === "choosing-subclass" && (
        <div className="space-y-2 border-t pt-3">
          <p className="font-medium text-sm">Choose a subclass:</p>
          {subclassOptions.length === 0 ? (
            <p className="text-muted-foreground text-sm">Loading options...</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {subclassOptions.map((opt) => (
                <Button
                  key={opt.id}
                  onClick={async () => {
                    setPhase("applying-subclass");
                    setErrorMsg(null);
                    setRefreshFailed(false);
                    try {
                      const ack = await callMcpTool(
                        "select_subclass_5e",
                        { actorId: pc.id, subclassProfileId: opt.id },
                        campaignId
                      );
                      if (
                        ack?.data &&
                        typeof ack.data === "object" &&
                        "variant" in ack.data
                      ) {
                        throw new Error(
                          `Couldn't apply subclass (${ack.data.variant}).`
                        );
                      }
                      setPhase("subclass-done");
                      try {
                        await mutate();
                        await onChange();
                      } catch {
                        setRefreshFailed(true);
                      }
                    } catch (e) {
                      setErrorMsg(
                        e instanceof Error
                          ? e.message
                          : "Subclass selection failed"
                      );
                      setPhase("error");
                    }
                  }}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          )}
          <Button
            onClick={() => setPhase("idle")}
            size="sm"
            type="button"
            variant="ghost"
          >
            Cancel
          </Button>
        </div>
      )}

      {phase === "applying-subclass" && (
        <p className="text-muted-foreground text-sm">Applying subclass…</p>
      )}

      {phase === "subclass-done" && (
        <div className="space-y-2 border-t pt-3">
          <p className="font-semibold text-foreground text-sm">
            Subclass applied!
          </p>
          {refreshFailed && (
            <p className="text-muted-foreground text-xs">
              Couldn't auto-refresh — reload to see updated stats.
            </p>
          )}
          <Button
            onClick={() => {
              setPhase("idle");
              setRefreshFailed(false);
            }}
            size="sm"
            type="button"
            variant="ghost"
          >
            Done
          </Button>
        </div>
      )}

      {/* ── Feat-slot section ─────────────────────────────────────────────── */}

      {featSlots.fightingStyle > 0 && phase === "idle" && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center gap-2">
            <Badge>Fighting Style available</Badge>
          </div>
          <Button
            onClick={() => openFeatChooser("fightingStyle")}
            size="sm"
            type="button"
            variant="outline"
          >
            Choose Fighting Style
          </Button>
        </div>
      )}

      {featSlots.general > 0 && phase === "idle" && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center gap-2">
            <Badge>Feat slot available</Badge>
          </div>
          <Button
            onClick={() => openFeatChooser("general")}
            size="sm"
            type="button"
            variant="outline"
          >
            Choose feat
          </Button>
        </div>
      )}

      {featSlots.origin > 0 && phase === "idle" && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center gap-2">
            <Badge>Origin feat available</Badge>
          </div>
          <Button
            onClick={() => openFeatChooser("origin")}
            size="sm"
            type="button"
            variant="outline"
          >
            Choose origin feat
          </Button>
        </div>
      )}

      {pendingProfileProficiencyChoices.length > 0 && phase === "idle" && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center gap-2">
            <Badge>Proficiency choice available</Badge>
          </div>
          {pendingProfileProficiencyChoices.map((choice) => (
            <Button
              key={choice.profileId}
              onClick={() => {
                setErrorMsg(null);
                setSelectedProfileChoiceId(choice.profileId);
                setProfileProficiencyChoices(new Set());
                setPhase("choosing-profile-proficiencies");
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              Choose {choice.label} proficiencies
            </Button>
          ))}
        </div>
      )}

      {sheet.pendingExpertiseChoices.length > 0 && phase === "idle" && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center gap-2">
            <Badge>Expertise choice available</Badge>
          </div>
          {sheet.pendingExpertiseChoices.map((choice) => (
            <Button
              key={choice.classProfileId}
              onClick={() => {
                setErrorMsg(null);
                setSelectedExpertiseChoice(choice);
                setSelectedExpertiseSkills([]);
                setSelectedExpertiseTools([]);
                setPhase("choosing-expertise");
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              Choose {choice.label} Expertise
            </Button>
          ))}
        </div>
      )}

      {sheet.pendingMetamagicChoices.length > 0 && phase === "idle" && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center gap-2">
            <Badge>Metamagic choice available</Badge>
          </div>
          {sheet.pendingMetamagicChoices.map((choice) => (
            <Button
              key={choice.classProfileId}
              onClick={() => {
                setErrorMsg(null);
                setSelectedMetamagicChoice(choice);
                setSelectedMetamagicOptions([]);
                setPhase("choosing-metamagic");
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              Choose {choice.label} Metamagic
            </Button>
          ))}
        </div>
      )}

      {sheet.pendingWeaponMasteryChoices.length > 0 && phase === "idle" && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center gap-2">
            <Badge>Weapon Mastery choice available</Badge>
          </div>
          {sheet.pendingWeaponMasteryChoices.map((choice) => (
            <Button
              key={choice.classProfileId}
              onClick={() => {
                setErrorMsg(null);
                setSelectedWeaponMasteryChoice(choice);
                setSelectedWeaponMasteries([]);
                setPhase("choosing-weapon-masteries");
                loadWeaponMasteryOptions().catch(() => undefined);
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              Choose {choice.label} Weapon Masteries
            </Button>
          ))}
        </div>
      )}

      <Dialog
        onOpenChange={(open) => {
          if (!open && featChoiceDialogOpen) {
            setPhase("idle");
            setSelectedFeatId(null);
            setSelectedFeatPool(null);
            setSelectedProfileChoiceId(null);
            setProfileProficiencyChoices(new Set());
            resetExpertiseSelection();
            resetMetamagicSelection();
            resetWeaponMasterySelection();
          }
        }}
        open={featChoiceDialogOpen}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Choose Advancement</DialogTitle>
            <DialogDescription>
              Complete the pending level-up choice for this character.
            </DialogDescription>
          </DialogHeader>

          {phase === "choosing-feat" && (
            <div className="space-y-2 border-t pt-3">
              <p className="font-medium text-sm">Choose a feat:</p>
              <div className="flex flex-wrap gap-2">
                {loadingFeatPool === "general" && (
                  <p className="text-muted-foreground text-sm">
                    Loading feats...
                  </p>
                )}
                {featOptions.general.map((feat) => (
                  <Button
                    key={feat.id}
                    onClick={() => handleFeatClick(feat, "general")}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    {feat.label}
                  </Button>
                ))}
              </div>
              <Button
                onClick={() => setPhase("idle")}
                size="sm"
                type="button"
                variant="ghost"
              >
                Cancel
              </Button>
            </div>
          )}

          {phase === "choosing-origin-feat" && (
            <div className="space-y-2 border-t pt-3">
              <p className="font-medium text-sm">Choose an origin feat:</p>
              <div className="flex flex-wrap gap-2">
                {loadingFeatPool === "origin" && (
                  <p className="text-muted-foreground text-sm">
                    Loading feats...
                  </p>
                )}
                {featOptions.origin.map((feat) => (
                  <Button
                    key={feat.id}
                    onClick={() => handleFeatClick(feat, "origin")}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    {feat.label}
                  </Button>
                ))}
              </div>
              <Button
                onClick={() => setPhase("idle")}
                size="sm"
                type="button"
                variant="ghost"
              >
                Cancel
              </Button>
            </div>
          )}

          {phase === "choosing-fighting-style-feat" && (
            <div className="space-y-2 border-t pt-3">
              <p className="font-medium text-sm">Choose a Fighting Style:</p>
              <div className="flex flex-wrap gap-2">
                {loadingFeatPool === "fightingStyle" && (
                  <p className="text-muted-foreground text-sm">
                    Loading feats...
                  </p>
                )}
                {featOptions.fightingStyle.map((feat) => (
                  <Button
                    key={feat.id}
                    onClick={() => handleFeatClick(feat, "fightingStyle")}
                    size="sm"
                    type="button"
                    variant={selectedFeatId === feat.id ? "default" : "outline"}
                  >
                    {feat.label}
                  </Button>
                ))}
              </div>
              <Button
                onClick={() => setPhase("idle")}
                size="sm"
                type="button"
                variant="ghost"
              >
                Cancel
              </Button>
            </div>
          )}

          {phase === "configuring-asi" && (
            <div className="space-y-3 border-t pt-3">
              <p className="font-medium text-sm">
                Ability Score Increase — distribute +2 points:
              </p>
              <div className="grid grid-cols-3 gap-2">
                {ASI_ABILITIES.map((ability) => {
                  const bump = asiConfig[ability] ?? 0;
                  const score = scoresFromSheet[ability] ?? 10;
                  return (
                    <div
                      className="flex items-center gap-1 text-sm"
                      key={ability}
                    >
                      <span className="w-8 uppercase">{ability}</span>
                      <span className="w-8 text-center text-muted-foreground">
                        {score}
                      </span>
                      <Button
                        className="h-6 w-6 p-0"
                        disabled={bump <= 0}
                        onClick={() => adjustAsi(ability, -1)}
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        −
                      </Button>
                      <span className="w-4 text-center">
                        {bump > 0 ? `+${bump}` : "—"}
                      </span>
                      <Button
                        className="h-6 w-6 p-0"
                        onClick={() => adjustAsi(ability, 1)}
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        +
                      </Button>
                    </div>
                  );
                })}
              </div>
              {!asiValidation.ok && asiValidation.reason && (
                <p className="text-muted-foreground text-xs">
                  {asiValidation.reason}
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  disabled={!asiValidation.ok}
                  onClick={() => {
                    if (selectedFeatId) {
                      applyFeat(selectedFeatId, asiConfig);
                    }
                  }}
                  size="sm"
                  type="button"
                >
                  Confirm
                </Button>
                <Button
                  onClick={() => {
                    setPhase(chooserPhaseForPool(selectedFeatPool));
                    setAsiConfig({});
                  }}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  Back
                </Button>
              </div>
            </div>
          )}

          {phase === "configuring-skilled" && (
            <div className="space-y-3 border-t pt-3">
              <p className="font-medium text-sm">
                Skilled — choose 3 skill proficiencies ({skilledChoices.size}/3
                selected):
              </p>
              <div className="flex flex-wrap gap-2">
                {SKILLED_SKILL_OPTIONS.map((skill) => {
                  const selected = skilledChoices.has(skill);
                  const atMax = skilledChoices.size >= 3 && !selected;
                  return (
                    <Button
                      aria-pressed={selected}
                      className={selected ? "ring-2 ring-primary" : ""}
                      disabled={atMax}
                      key={skill}
                      onClick={() => {
                        setSkilledChoices((prev) => {
                          const next = new Set(prev);
                          if (next.has(skill)) {
                            next.delete(skill);
                          } else {
                            next.add(skill);
                          }
                          return next;
                        });
                      }}
                      size="sm"
                      type="button"
                      variant={selected ? "default" : "outline"}
                    >
                      {skillLabel(skill)}
                    </Button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Button
                  disabled={skilledChoices.size !== 3}
                  onClick={() => {
                    if (selectedFeatId) {
                      applyFeat(selectedFeatId, {
                        skills: [...skilledChoices],
                      });
                    }
                  }}
                  size="sm"
                  type="button"
                >
                  Confirm
                </Button>
                <Button
                  onClick={() => {
                    setSkilledChoices(new Set());
                    setPhase(chooserPhaseForPool(selectedFeatPool));
                  }}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  Back
                </Button>
              </div>
            </div>
          )}

          {phase === "configuring-dependent-choice" && dependentField && (
            <div className="space-y-3 border-t pt-3">
              <p className="font-medium text-sm">{dependentField.label}</p>
              <div className="flex flex-wrap gap-2">
                {dependentField.options.map((option) => {
                  const selected = dependentSource === option.id;
                  return (
                    <Button
                      aria-pressed={selected}
                      key={option.id}
                      onClick={() => {
                        if (option.id !== dependentSource) {
                          setDependentSource(option.id);
                          setDependentSingles({});
                          setDependentMultis({});
                        }
                      }}
                      size="sm"
                      type="button"
                      variant={selected ? "default" : "outline"}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </div>

              {dependentSource !== null &&
                revealedDependentFields.map((field) => {
                  if (field.kind === "single") {
                    const selectedValue = dependentSingles[field.key] ?? "";
                    return (
                      <div className="space-y-2" key={field.key}>
                        <p className="font-medium text-sm">{field.label}</p>
                        <div className="flex flex-wrap gap-2">
                          {field.options.map((option) => {
                            const selected = selectedValue === option.id;
                            return (
                              <Button
                                aria-pressed={selected}
                                key={option.id}
                                onClick={() =>
                                  setDependentSingles((prev) => ({
                                    ...prev,
                                    [field.key]: option.id,
                                  }))
                                }
                                size="sm"
                                type="button"
                                variant={selected ? "default" : "outline"}
                              >
                                {option.label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  if (field.kind === "multi") {
                    const selectedValues = dependentMultis[field.key] ?? [];
                    return (
                      <div className="space-y-2" key={field.key}>
                        <p className="font-medium text-sm">
                          {field.label} ({selectedValues.length}/{field.count}{" "}
                          selected)
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {field.options.map((option) => {
                            const selected = selectedValues.includes(option.id);
                            const atMax =
                              selectedValues.length >= field.count && !selected;
                            return (
                              <Button
                                aria-pressed={selected}
                                disabled={atMax}
                                key={option.id}
                                onClick={() =>
                                  setDependentMultis((prev) => {
                                    const current = prev[field.key] ?? [];
                                    return {
                                      ...prev,
                                      [field.key]: selected
                                        ? current.filter(
                                            (id) => id !== option.id
                                          )
                                        : [...current, option.id],
                                    };
                                  })
                                }
                                size="sm"
                                type="button"
                                variant={selected ? "default" : "outline"}
                              >
                                {option.label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  return null;
                })}

              {dependentChoiceError && (
                <p className="text-destructive text-sm">
                  {dependentChoiceError}
                </p>
              )}

              <div className="flex gap-2">
                <Button
                  disabled={!dependentChoicesComplete}
                  onClick={() => {
                    if (!selectedFeatId) {
                      return;
                    }
                    const config = buildDependentConfig();
                    if (config) {
                      applyFeat(selectedFeatId, config);
                    }
                  }}
                  size="sm"
                  type="button"
                >
                  Confirm
                </Button>
                <Button
                  onClick={() => {
                    setDependentField(null);
                    setDependentSource(null);
                    setDependentSingles({});
                    setDependentMultis({});
                    setPhase(chooserPhaseForPool(selectedFeatPool));
                  }}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  Back
                </Button>
              </div>
            </div>
          )}

          {phase === "choosing-profile-proficiencies" &&
            selectedProfileChoice && (
              <div className="space-y-3 border-t pt-3">
                <p className="font-medium text-sm">
                  {selectedProfileChoice.label} — choose{" "}
                  {selectedProfileChoice.count} proficiencies (
                  {profileProficiencyChoices.size}/{selectedProfileChoice.count}
                  selected):
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedProfileChoice.pool.map((proficiency) => {
                    const selected = profileProficiencyChoices.has(proficiency);
                    const atMax =
                      profileProficiencyChoices.size >=
                        selectedProfileChoice.count && !selected;
                    return (
                      <Button
                        aria-pressed={selected}
                        disabled={atMax}
                        key={proficiency}
                        onClick={() => {
                          setProfileProficiencyChoices((prev) => {
                            const next = new Set(prev);
                            if (next.has(proficiency)) {
                              next.delete(proficiency);
                            } else {
                              next.add(proficiency);
                            }
                            return next;
                          });
                        }}
                        size="sm"
                        type="button"
                        variant={selected ? "default" : "outline"}
                      >
                        {skillLabel(proficiency)}
                      </Button>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <Button
                    disabled={!profileChoiceComplete}
                    onClick={applyProfileProficiencies}
                    size="sm"
                    type="button"
                  >
                    Confirm
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedProfileChoiceId(null);
                      setProfileProficiencyChoices(new Set());
                      setPhase("idle");
                    }}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

          {phase === "choosing-expertise" && selectedExpertiseChoice && (
            <div className="space-y-3 border-t pt-3">
              <p className="font-medium text-sm">
                {selectedExpertiseChoice.label} Expertise — choose{" "}
                {selectedExpertiseChoice.remaining} options (
                {selectedExpertiseCount}/{selectedExpertiseChoice.remaining}{" "}
                selected):
              </p>
              {selectedExpertiseChoice.skillPool.length > 0 && (
                <div className="space-y-2">
                  <p className="text-muted-foreground text-xs">Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedExpertiseChoice.skillPool.map((skill) => {
                      const selected = selectedExpertiseSkills.includes(skill);
                      const atMax =
                        selectedExpertiseCount >=
                          selectedExpertiseChoice.remaining && !selected;
                      return (
                        <Button
                          aria-pressed={selected}
                          disabled={atMax}
                          key={skill}
                          onClick={() => {
                            setSelectedExpertiseSkills((prev) =>
                              selected
                                ? prev.filter((choice) => choice !== skill)
                                : [...prev, skill]
                            );
                          }}
                          size="sm"
                          type="button"
                          variant={selected ? "default" : "outline"}
                        >
                          {skillLabel(skill)}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
              {selectedExpertiseChoice.toolPool.length > 0 && (
                <div className="space-y-2">
                  <p className="text-muted-foreground text-xs">Tools</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedExpertiseChoice.toolPool.map((tool) => {
                      const selected = selectedExpertiseTools.includes(tool);
                      const atMax =
                        selectedExpertiseCount >=
                          selectedExpertiseChoice.remaining && !selected;
                      return (
                        <Button
                          aria-pressed={selected}
                          disabled={atMax}
                          key={tool}
                          onClick={() => {
                            setSelectedExpertiseTools((prev) =>
                              selected
                                ? prev.filter((choice) => choice !== tool)
                                : [...prev, tool]
                            );
                          }}
                          size="sm"
                          type="button"
                          variant={selected ? "default" : "outline"}
                        >
                          {skillLabel(tool)}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  disabled={!expertiseChoiceComplete}
                  onClick={applyExpertise}
                  size="sm"
                  type="button"
                >
                  Confirm
                </Button>
                <Button
                  onClick={() => {
                    resetExpertiseSelection();
                    setPhase("idle");
                  }}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {phase === "choosing-metamagic" && selectedMetamagicChoice && (
            <div className="space-y-3 border-t pt-3">
              <p className="font-medium text-sm">
                {selectedMetamagicChoice.label} Metamagic — choose{" "}
                {selectedMetamagicChoice.remaining} options (
                {selectedMetamagicOptions.length}/
                {selectedMetamagicChoice.remaining} selected):
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedMetamagicChoice.optionPool.map((optionId) => {
                  const selected = selectedMetamagicOptions.includes(optionId);
                  const atMax =
                    selectedMetamagicOptions.length >=
                      selectedMetamagicChoice.remaining && !selected;
                  return (
                    <Button
                      aria-pressed={selected}
                      disabled={atMax}
                      key={optionId}
                      onClick={() => {
                        setSelectedMetamagicOptions((prev) =>
                          selected
                            ? prev.filter((choice) => choice !== optionId)
                            : [...prev, optionId]
                        );
                      }}
                      size="sm"
                      type="button"
                      variant={selected ? "default" : "outline"}
                    >
                      {optionLabel(optionId)}
                    </Button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Button
                  disabled={!metamagicChoiceComplete}
                  onClick={applyMetamagic}
                  size="sm"
                  type="button"
                >
                  Confirm
                </Button>
                <Button
                  onClick={() => {
                    resetMetamagicSelection();
                    setPhase("idle");
                  }}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {phase === "choosing-weapon-masteries" &&
            selectedWeaponMasteryChoice && (
              <div className="space-y-3 border-t pt-3">
                <p className="font-medium text-sm">
                  {selectedWeaponMasteryChoice.label} Weapon Masteries — choose{" "}
                  {selectedWeaponMasteryChoice.remaining} weapons (
                  {selectedWeaponMasteries.length}/
                  {selectedWeaponMasteryChoice.remaining} selected):
                </p>
                {loadingWeaponMasteries ? (
                  <p className="text-muted-foreground text-sm">
                    Loading weapon masteries...
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {availableWeaponMasteryOptions.map((option) => {
                      const selected = selectedWeaponMasteries.includes(
                        option.weaponKind
                      );
                      const atMax =
                        selectedWeaponMasteries.length >=
                          selectedWeaponMasteryChoice.remaining && !selected;
                      return (
                        <Button
                          aria-pressed={selected}
                          className="h-auto justify-start whitespace-normal px-3 py-2 text-left"
                          disabled={atMax}
                          key={option.weaponKind}
                          onClick={() => {
                            setSelectedWeaponMasteries((prev) =>
                              selected
                                ? prev.filter(
                                    (choice) => choice !== option.weaponKind
                                  )
                                : [...prev, option.weaponKind]
                            );
                          }}
                          size="sm"
                          type="button"
                          variant={selected ? "default" : "outline"}
                        >
                          <span className="flex flex-col items-start gap-1">
                            <span>{option.label}</span>
                            <span className="text-xs opacity-80">
                              {masteryLabel(option.mastery)} mastery
                            </span>
                          </span>
                        </Button>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    disabled={!weaponMasteryChoiceComplete}
                    onClick={applyWeaponMasteries}
                    size="sm"
                    type="button"
                  >
                    Confirm
                  </Button>
                  <Button
                    onClick={() => {
                      resetWeaponMasterySelection();
                      setPhase("idle");
                    }}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

          {phase === "applying-feat" && (
            <p className="text-muted-foreground text-sm">Applying feat…</p>
          )}

          {phase === "applying-profile-proficiencies" && (
            <p className="text-muted-foreground text-sm">
              Applying proficiencies…
            </p>
          )}

          {phase === "applying-expertise" && (
            <p className="text-muted-foreground text-sm">Applying Expertise…</p>
          )}

          {phase === "applying-metamagic" && (
            <p className="text-muted-foreground text-sm">Applying Metamagic…</p>
          )}

          {phase === "applying-weapon-masteries" && (
            <p className="text-muted-foreground text-sm">
              Applying Weapon Masteries…
            </p>
          )}

          {phase === "feat-done" && (
            <div className="space-y-2 border-t pt-3">
              <p className="font-semibold text-foreground text-sm">
                Feat applied!
              </p>
              {refreshFailed && (
                <p className="text-muted-foreground text-xs">
                  Couldn't auto-refresh the sheet — reload to see updated stats.
                </p>
              )}
              <Button
                onClick={() => {
                  setPhase("idle");
                  setSelectedFeatId(null);
                  setSelectedFeatPool(null);
                  setAsiConfig({});
                  setSkilledChoices(new Set());
                  setDependentField(null);
                  setDependentSource(null);
                  setDependentSingles({});
                  setDependentMultis({});
                  setRefreshFailed(false);
                }}
                size="sm"
                type="button"
                variant="ghost"
              >
                Done
              </Button>
            </div>
          )}

          {phase === "profile-proficiencies-done" && (
            <div className="space-y-2 border-t pt-3">
              <p className="font-semibold text-foreground text-sm">
                Proficiencies applied!
              </p>
              {refreshFailed && (
                <p className="text-muted-foreground text-xs">
                  Couldn't auto-refresh the sheet — reload to see updated stats.
                </p>
              )}
              <Button
                onClick={() => {
                  setPhase("idle");
                  setSelectedProfileChoiceId(null);
                  setProfileProficiencyChoices(new Set());
                  setRefreshFailed(false);
                }}
                size="sm"
                type="button"
                variant="ghost"
              >
                Done
              </Button>
            </div>
          )}

          {phase === "expertise-done" && (
            <div className="space-y-2 border-t pt-3">
              <p className="font-semibold text-foreground text-sm">
                Expertise applied!
              </p>
              {refreshFailed && (
                <p className="text-muted-foreground text-xs">
                  Couldn't auto-refresh the sheet — reload to see updated stats.
                </p>
              )}
              <Button
                onClick={() => {
                  setPhase("idle");
                  resetExpertiseSelection();
                  setRefreshFailed(false);
                }}
                size="sm"
                type="button"
                variant="ghost"
              >
                Done
              </Button>
            </div>
          )}

          {phase === "metamagic-done" && (
            <div className="space-y-2 border-t pt-3">
              <p className="font-semibold text-foreground text-sm">
                Metamagic applied!
              </p>
              {refreshFailed && (
                <p className="text-muted-foreground text-xs">
                  Couldn't auto-refresh the sheet — reload to see updated stats.
                </p>
              )}
              <Button
                onClick={() => {
                  setPhase("idle");
                  resetMetamagicSelection();
                  setRefreshFailed(false);
                }}
                size="sm"
                type="button"
                variant="ghost"
              >
                Done
              </Button>
            </div>
          )}

          {phase === "weapon-masteries-done" && (
            <div className="space-y-2 border-t pt-3">
              <p className="font-semibold text-foreground text-sm">
                Weapon Masteries applied!
              </p>
              {refreshFailed && (
                <p className="text-muted-foreground text-xs">
                  Couldn't auto-refresh the sheet — reload to see updated stats.
                </p>
              )}
              <Button
                onClick={() => {
                  setPhase("idle");
                  resetWeaponMasterySelection();
                  setRefreshFailed(false);
                }}
                size="sm"
                type="button"
                variant="ghost"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {phase === "error" && (
        <div className="space-y-2">
          <p className="text-destructive text-sm">{errorMsg}</p>
          <Button
            onClick={() => {
              setPhase("idle");
              setDependentField(null);
              setDependentSource(null);
              setDependentSingles({});
              setDependentMultis({});
              resetExpertiseSelection();
              resetMetamagicSelection();
              resetWeaponMasterySelection();
            }}
            size="sm"
            type="button"
            variant="ghost"
          >
            Dismiss
          </Button>
        </div>
      )}
    </div>
  );
}
